import { useState } from "react";
import { Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { useMember } from "../../context/memberContext";
import { useTranslations } from "../../hooks/useTranslations";
import { SP } from "../../http/serviceportalApi";
import { departmentSlugs } from "../../types";
import { includesAny } from "../../utils";
import {
  accountingDocumentPermissions,
  payrollDocumentPermissions,
} from "../member/DocumentPermissions";
import Alert from "../ui/Alert";
import NavTabs from "../ui/NavTabs";
import Page from "../ui/Page";
import Select from "../ui/Select";
import styles from "./Documents.module.css";
import DocumentsTab from "./DocumentsTab";
import { getAuthorizedLuidsByDocumentSet } from "./utils";

interface Option {
  luid: SP.Luid;
  name: string;
  permissions: string[];
}

const Documents = () => {
  const { i18n } = useTranslations();
  const { member } = useMember();
  const navigate = useNavigate();
  const options = getOptions(member, i18n);
  const [selectedOption, setSelectedOption] = useState(options[0]);

  const setSelectedOptionByLuid = (luid: SP.Luid) => {
    const selected = options.find((o) => o.luid === luid);
    setSelectedOption(selected);
  };

  return (
    <Page title={i18n.documents.title}>
      <Alert showUntil={new Date(2026, 7, 12, 18, 0)}>
        {i18n.documents.maintenanceAlert("12:00", "18:00")}
      </Alert>
      {options.length > 1 && (
        <Select
          className={styles.optionSelect}
          value={selectedOption?.luid}
          onChange={(e) => {
            setSelectedOptionByLuid(+e.target.value);
            navigate("/dokumente");
          }}
        >
          {options.map(({ luid, name }) => (
            <option key={luid} value={luid}>
              {name}
            </option>
          ))}
        </Select>
      )}
      {selectedOption && (
        <OptionDocuments
          allOptions={options}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOptionByLuid}
        />
      )}
    </Page>
  );
};

interface OptionDocumentsProps {
  allOptions: Option[];
  selectedOption: Option;
  setSelectedOption: (luid: SP.Luid) => void;
}

const OptionDocuments = ({
  allOptions,
  selectedOption,
  setSelectedOption,
}: OptionDocumentsProps) => {
  const { i18n } = useTranslations();
  const { member } = useMember();
  const allArchives = getArchives(allOptions.flatMap((o) => o.permissions));
  const selectedOptionArchives = getArchives(selectedOption.permissions);
  const firstArchive = selectedOptionArchives[0];
  const allLuids = allOptions.map((o) => o.luid);

  return (
    <>
      <NavTabs
        tabs={selectedOptionArchives.map((a) => ({
          to: departmentSlugs[a],
          text: i18n.departments[a].title,
        }))}
      />

      <Routes>
        {allArchives.map((a) => (
          <Route
            key={a}
            path={`${departmentSlugs[a]}/*`}
            element={
              <DocumentsTab
                luid={selectedOption.luid}
                allLuids={allLuids}
                archive={a}
                setSelectedOption={setSelectedOption}
                getAuthorizedLuids={(set) =>
                  member ? getAuthorizedLuidsByDocumentSet(member, set) : []
                }
              />
            }
          />
        ))}
        {firstArchive && (
          <Route
            path="*"
            element={
              <Navigate to={departmentSlugs[firstArchive]} replace={true} />
            }
          />
        )}
      </Routes>
    </>
  );
};

const getOptions = (
  member: SP.Member | undefined,
  i18n: ReturnType<typeof useTranslations>["i18n"],
): Option[] => {
  const options: Option[] = [];

  if (
    member?.luid !== undefined &&
    member.permissions &&
    includesAny(member.permissions, [
      ...accountingDocumentPermissions,
      ...payrollDocumentPermissions,
    ])
  ) {
    options.push({
      luid: member.luid,
      name: i18n.allCompanies,
      permissions: member.permissions,
    });
  }

  member?.companies
    ?.sort((a, b) => a.name.localeCompare(b.name))
    .forEach((value) => {
      if (
        value.luid !== undefined &&
        value.permissions &&
        includesAny(value.permissions, [
          ...accountingDocumentPermissions,
          ...payrollDocumentPermissions,
        ])
      )
        options.push({
          luid: value.luid,
          name: value.name,
          permissions: value.permissions,
        });
    });

  return options;
};

const getArchives = (permissions: string[]) => {
  const archives: SP.DocumentArchive[] = [];
  if (includesAny(permissions, payrollDocumentPermissions))
    archives.push("payroll");
  if (includesAny(permissions, accountingDocumentPermissions))
    archives.push("accounting");
  return archives;
};

export default Documents;
